import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import api from "services/api";

/**
 * Monitoring setup (2026-10-21).
 *
 * Answers "is monitoring actually working?" — the most common silent failure
 * is a collector that was never installed, which otherwise looks identical to
 * a perfectly healthy server.
 */

const Row = ({ label, value, state }) => {
  const theme = useTheme();

  const icon =
    state === "ok" ? (
      <CheckCircleRoundedIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
    ) : state === "warn" ? (
      <WarningRoundedIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
    ) : state === "error" ? (
      <ErrorRoundedIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
    ) : null;

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.75 }}>
      <Box sx={{ mt: "2px", display: "flex", flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const Section = ({ title, children }) => {
  const theme = useTheme();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: "12px",
        height: "100%",
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
};

const ServerSetup = () => {
  const theme = useTheme();

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [configResponse, statusResponse] = await Promise.all([
        api.get("/server/config"),
        api.get("/server/status"),
      ]);
      setConfig(configResponse.data || null);
      setStatus(statusResponse.data || null);
    } catch {
      // Leave the cards in their unknown state.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const snapshot = status?.snapshot || null;
  const staleness = status?.staleness || {};
  const collectorRunning = Boolean(snapshot) && !staleness.isStale;

  const services = Array.isArray(snapshot?.Services) ? snapshot.Services : [];
  const probeFailures = services.filter((service) => service.status === "unknown");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: "12px",
          bgcolor: collectorRunning
            ? `${theme.palette.success.main}0d`
            : `${theme.palette.error.main}0d`,
          borderColor: collectorRunning
            ? `${theme.palette.success.main}55`
            : `${theme.palette.error.main}55`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {collectorRunning ? (
            <CheckCircleRoundedIcon sx={{ color: theme.palette.success.main }} />
          ) : (
            <ErrorRoundedIcon sx={{ color: theme.palette.error.main }} />
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {collectorRunning ? "Collector is running" : "Collector is NOT reporting"}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {collectorRunning
            ? `Last snapshot ${staleness.ageSeconds}s ago. Expected every 60s.`
            : snapshot
              ? `Last snapshot was ${Math.round((staleness.ageSeconds || 0) / 60)} minute(s) ago — check the system cron job.`
              : "No snapshot has ever been collected. Install the cron job (see below)."}
        </Typography>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title="Collector">
            <Row
              label="Schedule"
              value="System cron, every minute — survives app restarts"
              state="ok"
            />
            <Row
              label="Stale threshold"
              value={`${config?.staleAfterSeconds || 180}s (3 missed runs)`}
              state="ok"
            />
            <Row
              label="Retention"
              value={`${config?.metricsRetentionDays || 14} days of raw snapshots`}
              state="ok"
            />
            <Row
              label="Last collection"
              value={
                snapshot?.CapturedAt
                  ? `${new Date(snapshot.CapturedAt).toLocaleString()} (${snapshot.CollectedBy || "cron"})`
                  : "never"
              }
              state={collectorRunning ? "ok" : "error"}
            />
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Section title="Monitored paths">
            <Row
              label="Disk mount"
              value={config?.diskMount || "/"}
              state={snapshot?.DiskPercent !== null && snapshot?.DiskPercent !== undefined ? "ok" : "warn"}
            />
            <Row
              label="Backup directory"
              value={config?.backupDirectory || "/db_backups"}
              state={
                snapshot?.Backups?.status === "ok"
                  ? "ok"
                  : snapshot?.Backups?.status === "unavailable"
                    ? "error"
                    : "warn"
              }
            />
            <Row
              label="SSL certificates"
              value={
                config?.sslConfigured
                  ? (config.sslDomains || []).join(", ") || "configured"
                  : "Not configured — set SERVER_SSL_CERTS to monitor expiry"
              }
              state={config?.sslConfigured ? "ok" : "warn"}
            />
          </Section>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Section title="Service probes">
            {services.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No service probes in the latest snapshot.
              </Typography>
            )}
            {services.map((service) => (
              <Row
                key={service.name}
                label={`${service.name} — ${service.status}`}
                value={service.detail}
                state={service.status === "running" ? "ok" : "warn"}
              />
            ))}

            {probeFailures.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  label={`${probeFailures.length} probe(s) could not resolve`}
                  sx={{
                    height: 22,
                    borderRadius: "6px",
                    fontSize: "0.7rem",
                    bgcolor: `${theme.palette.warning.main}1a`,
                    color: theme.palette.warning.main,
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.75 }}
                >
                  “Unknown” means neither a systemd unit nor a listening port was
                  found. That is usually a wrong unit name — not an outage.
                </Typography>
              </Box>
            )}
          </Section>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Section title="Install the collector on the VPS">
            <Typography
              variant="body2"
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                m: 0,
                p: 1.5,
                borderRadius: "8px",
                bgcolor: theme.palette.action.hover,
              }}
            >
              {`# 1. Migration
cd /var/www/ce_portal_server && npx sequelize db:migrate

# 2. Cron (as the app user, not root)
crontab -e
* * * * * cd /var/www/ce_portal_server && /usr/bin/node scripts/collect_server_metrics.js >> logs/metrics-cron.log 2>&1

# 3. Verify
tail -f /var/www/ce_portal_server/logs/metrics-cron.log`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Full guide with sudoers notes:{" "}
              <code>server/docs/server-monitoring-setup.md</code>
            </Typography>
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ServerSetup;
